/**
 * What an «ukjent» or «delvis» send to Tripletex or PowerOffice contained (PROM-23, kept on the send row) set
 * against the month's lines now, as the status answers it (`scope`, payMonth.compareSend): the month, people and
 * version, then «Samme innhold som nå» or «Endret siden» with the counts. Reading only: whether the month may go
 * again is still the release's «Kom ikke fram, send på nytt».
 */
import { T } from '@/i18n';
import { monthLabel } from '@/ui/format';

export interface HeldSendScope {
  start: string;
  end: string;
  stores: number;
  people: number;
  /** 7 characters of the lines' revision */
  revision: string | null;
  /** null: the lines now could not be read for this person */
  same_as_now: boolean | null;
  now?: { people: number; revision: string; added: number; removed: number } | undefined;
}

const ansatte = (n: number) => T('{n, plural, one {# ansatt} other {# ansatte}}', { n });

export function HeldScope({ scope }: { scope?: HeldSendScope | undefined }) {
  if (!scope) return null;
  const what = [
    monthLabel(scope.start.slice(0, 7)),
    ansatte(scope.people),
    ...(scope.stores > 1 ? [T('{n} butikker', { n: scope.stores })] : []),
    ...(scope.revision ? [T('versjon {rev}', { rev: scope.revision })] : []),
  ].join(', ');
  const now = scope.now;
  return (
    <span className="lnote" data-testid="held-scope">
      {T('Sendingen gjaldt {what}.', { what })}{' '}
      {scope.same_as_now == null || !now
        ? T('Kan ikke sammenlignes med timene nå.')
        : scope.same_as_now
          ? T('Samme innhold som nå.')
          : now.added || now.removed
            ? T(
                'Endret siden: {sent} i sendingen, {now} nå ({added, plural, one {# ny} other {# nye}}, {removed} ikke lenger med).',
                {
                  sent: ansatte(scope.people),
                  now: ansatte(now.people),
                  added: now.added,
                  removed: now.removed,
                },
              )
            : T(
                'Endret siden: de samme {people}, men timene eller satsene er ikke de samme nå (versjon {rev}).',
                {
                  people: ansatte(scope.people),
                  rev: now.revision,
                },
              )}
    </span>
  );
}
